import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  HardDrive,
  RefreshCw,
  AlertTriangle,
  Database,
  Layers,
  Check,
  Copy,
  Search,
  Sparkles,
  Table,
  TableOfContents,
  Clock,
  TrendingUp,
  Zap,
  CheckCircle2,
  Server,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { CustomDropdown } from '../../../components/CustomDropdown ';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  ComposedChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';

/* ─────────────────────────────────────────────────────────────
   TYPES & INTERFACES (Matching Real MySQL Backend Telemetry)
   ───────────────────────────────────────────────────────────── */

export interface MySqlStorageProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

export interface MySqlKPIs {
  total_size_mb?: number | null;
  data_mb?: number | null;
  index_mb?: number | null;
  reclaimable_mb?: number | null;
  tables?: number | null;
  indexes?: number | null;
  auto_increment_max_pct?: number | null;
  open_alerts?: {
    total?: number | null;
    warnings?: number | null;
    critical?: number | null;
  };
}

export interface MySqlEngineItem {
  engine: string;
  tables?: number | null;
  data_mb?: number | null;
  index_mb?: number | null;
  free_mb?: number | null;
  est_rows?: number | null;
  databases?: string[];
  size_mb?: number | null;
  size_pct?: number | null;
}

export interface MySqlDatabaseItem {
  database: string;
  is_system?: boolean;
  size_mb?: number | null;
  data_mb?: number | null;
  index_mb?: number | null;
  free_mb?: number | null;
  tables?: number | null;
  views?: number | null;
  row_estimate?: number | null;
  size_source?: string;
  engines?: Array<{
    engine: string;
    data_mb?: number | null;
    index_mb?: number | null;
    free_mb?: number | null;
    est_rows?: number | null;
    tables?: number | null;
  }>;
  fragmented_tables?: number | null;
  tables_without_pk?: number | null;
  auto_increment_max_pct?: number | null;
  index_count?: number | null;
  ignored_indexes?: number | null;
  unused_indexes?: number | null;
  redundant_indexes?: number | null;
  stats_missing_tables?: number | null;
  maint_checked_at?: string | null;
  status?: string;
  status_reasons?: Array<{
    severity: string;
    reason: string;
  }>;
}

export interface MySqlTableItem {
  table: string;
  database: string;
  engine?: string;
  data_mb?: number | null;
  index_mb?: number | null;
  free_mb?: number | null;
  est_rows?: number | null;
  avg_row_bytes?: number | null;
  row_format?: string;
  collation?: string;
  auto_increment?: number | null;
  created_at?: string;
  updated_at?: string;
  stats_rows?: number | null;
  stats_updated_at?: string;
}

export interface MySqlIndexItem {
  database?: string;
  table: string;
  index: string;
  columns?: string[] | string;
  cardinality?: number | null;
  reads?: number | null;
  writes?: number | null;
  size_mb?: number | null;
  type?: string;
  unique?: boolean;
  unused?: boolean;
  redundant_of?: string;
  flag?: string;
}

export interface MySqlFindingItem {
  severity: 'warning' | 'critical' | 'info' | string;
  category?: string;
  database?: string | null;
  object?: string | null;
  finding: string;
  impact?: string;
  remediation?: string;
  command?: string;
}

export interface MySqlStorageResponse {
  status: boolean;
  applicable?: boolean;
  kpis?: MySqlKPIs;
  engines?: MySqlEngineItem[];
  databases?: MySqlDatabaseItem[];
  largest_tables?: MySqlTableItem[];
  fragmentation?: {
    tables?: number;
    reclaimable_mb?: number;
    list?: any[];
    note?: string;
  };
  auto_increment?: {
    columns?: any;
    max_used_pct?: number | null;
    list?: any[];
    available?: boolean;
    reason?: string;
  };
  indexes?: {
    total?: number;
    ignored?: number;
    unused?: number;
    redundant?: number;
    usage_source?: string | null;
    usage_since?: string | null;
    ignored_list?: any[];
    unused_list?: MySqlIndexItem[];
    redundant_list?: MySqlIndexItem[];
    definitions?: MySqlIndexItem[];
  };
  missing_persistent_stats?: number;
  binlog?: {
    enabled?: boolean;
    files?: number;
    size_mb?: number;
    expire_sec?: number;
  };
  innodb?: {
    history_list_length?: number;
    system_tablespace_mb?: number;
    file_per_table?: boolean;
  };
  temp_tables_to_disk_pct?: number;
  coverage?: {
    databases?: number;
    databases_checked?: number;
    oldest_check?: string;
    interval_hours?: number;
    unavailable?: Record<string, any>;
  };
  events?: Array<{
    time: string;
    database: string;
    event: string;
    severity: string;
    details: string;
  }>;
  thresholds?: {
    auto_increment_warn_pct?: number;
    auto_increment_critical_pct?: number;
    fragmented_min_free_mb?: number;
    fragmented_min_pct?: number;
    history_list_warn?: number;
    temp_tables_to_disk_warn_pct?: number;
  };
  db_type?: string;
  findings?: {
    note?: string;
    count?: number;
    by_severity?: {
      warning?: number;
      info?: number;
      critical?: number;
    };
    items?: MySqlFindingItem[];
  };
}

/* ─────────────────────────────────────────────────────────────
   HELPERS & PALETTES
   ───────────────────────────────────────────────────────────── */

const PALETTE = ['#f97316', '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#64748b'];

const formatSize = (mb: number | null | undefined): string => {
  if (mb == null || isNaN(mb)) return '0 MB';
  if (mb >= 1024 * 1024) return `${(mb / (1024 * 1024)).toFixed(2)} TB`;
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  if (mb > 0) return `${(mb * 1024).toFixed(1)} KB`;
  return '0 MB';
};

const formatNumber = (num: number | null | undefined): string => {
  if (num == null || isNaN(num)) return '0';
  return num.toLocaleString();
};

const formatDateTime = (isoStr: string | null | undefined): string => {
  if (!isoStr) return '–';
  try {
    const d = new Date(isoStr);
    return isNaN(d.getTime()) ? isoStr : d.toLocaleString();
  } catch {
    return isoStr;
  }
};

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT: MySqlStorage
   ───────────────────────────────────────────────────────────── */

export const MySqlStorage: React.FC<MySqlStorageProps> = ({
  activeCluster,
  timeRange = '60',
  clientId,
  refreshKey = 0,
  dbType = 'mysql',
}) => {
  const [data, setData] = useState<MySqlStorageResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);


  // Filtering & Tab Navigation
  const [selectedDb, setSelectedDb] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'databases' | 'tables' | 'indexes' | 'findings' | 'coverage' | 'events'>('databases');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [indexFilter, setIndexFilter] = useState<'all' | 'unused' | 'redundant'>('all');
  const [copiedCmd, setCopiedCmd] = useState<string | null>(null);

  // Dynamic Dark Mode Observer for Recharts
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return false;
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const chartTheme = useMemo(() => ({
    grid: isDark ? '#374151' : '#e5e7eb',
    gridOpacity: isDark ? 0.4 : 0.6,
    axisStroke: isDark ? '#6b7280' : '#9ca3af',
    axisText: isDark ? '#d1d5db' : '#4b5563',
    tooltip: {
      backgroundColor: isDark ? '#1f2937' : '#ffffff',
      borderColor: isDark ? '#374151' : '#e5e7eb',
      borderRadius: '8px',
      color: isDark ? '#f3f4f6' : '#111827',
      fontSize: '12px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    tooltipItem: { color: isDark ? '#f3f4f6' : '#111827' },
    tooltipLabel: { color: isDark ? '#9ca3af' : '#4b5563', fontWeight: 600 },
    pieStroke: isDark ? '#1f2937' : '#ffffff',
  }), [isDark]);

  /* ─────────────────────────────────────────────────────────────
     DATA FETCHING
     ───────────────────────────────────────────────────────────── */

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);
    setError(null);
    try {
      const minutes = parseInt(timeRange || '60', 10);
      const params: Record<string, any> = {
        cluster_id: activeCluster,
        minutes: isNaN(minutes) ? 60 : minutes,
      };
      if (clientId) {
        params.client_id = clientId;
      }
      const res = await baseapi.get('/db_monitor_dashboard/mysql/storage-maintenance/overview', {
        params,
      });
      if (res.data) {
        setData(res.data);
      }
    } catch (err: any) {
      console.error('Failed to load MySQL storage & maintenance data:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to fetch MySQL storage overview');
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  /* ─────────────────────────────────────────────────────────────
     MEMOIZED DATA ACCESSORS
     ───────────────────────────────────────────────────────────── */

  const kpis = data?.kpis;
  const databases = useMemo(() => data?.databases || [], [data?.databases]);
  const largestTables = useMemo(() => data?.largest_tables || [], [data?.largest_tables]);
  const indexes = data?.indexes;
  const findings = data?.findings;
  const binlog = data?.binlog;
  const innodb = data?.innodb;
  const coverage = data?.coverage;
  const events = useMemo(() => data?.events || [], [data?.events]);

  // Database options for CustomDropdown
  const dbOptions = useMemo(() => {
    const list = new Set<string>();
    databases.forEach((d) => {
      if (d.database) list.add(d.database);
    });
    largestTables.forEach((t) => {
      if (t.database) list.add(t.database);
    });
    (indexes?.definitions || []).forEach((ix) => {
      if (ix.database) list.add(ix.database);
    });
    return Array.from(list).sort();
  }, [databases, largestTables, indexes?.definitions]);

  const dbDropdownOptions = useMemo(() => [
    { label: `All databases (${dbOptions.length})`, value: 'all' },
    ...dbOptions.map((name) => ({ label: name, value: name })),
  ], [dbOptions]);

  // Filtered Databases
  const filteredDatabases = useMemo(() => {
    return databases.filter((d) => {
      const matchDb = selectedDb === 'all' || d.database.toLowerCase() === selectedDb.toLowerCase();
      const matchSearch =
        !searchQuery.trim() ||
        d.database.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (d.status && d.status.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchDb && matchSearch;
    });
  }, [databases, selectedDb, searchQuery]);

  // Filtered Tables
  const filteredTables = useMemo(() => {
    return largestTables.filter((t) => {
      const matchDb = selectedDb === 'all' || t.database.toLowerCase() === selectedDb.toLowerCase();
      const matchSearch =
        !searchQuery.trim() ||
        t.table.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.database.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.engine && t.engine.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchDb && matchSearch;
    });
  }, [largestTables, selectedDb, searchQuery]);

  // Filtered Indexes
  const filteredIndexes = useMemo(() => {
    let list: MySqlIndexItem[] = [];
    if (indexFilter === 'unused') {
      list = (indexes?.unused_list || []).map((x) => ({ ...x, flag: 'unused' }));
    } else if (indexFilter === 'redundant') {
      list = (indexes?.redundant_list || []).map((x) => ({ ...x, flag: 'redundant' }));
    } else {
      const unusedKeys = new Set(
        (indexes?.unused_list || []).map((x) => `${x.database || ''}.${x.table}.${x.index}`)
      );
      const redundantKeys = new Set(
        (indexes?.redundant_list || []).map((x) => `${x.database || ''}.${x.table}.${x.index}`)
      );
      list = (indexes?.definitions || []).map((x) => {
        const key = `${x.database || ''}.${x.table}.${x.index}`;
        let flag = '';
        if (unusedKeys.has(key)) flag = 'unused';
        else if (redundantKeys.has(key)) flag = 'redundant';
        else if (x.unique) flag = 'unique';
        return { ...x, flag };
      });
    }

    return list.filter((ix) => {
      const db = ix.database || '';
      const matchDb = selectedDb === 'all' || !db || db.toLowerCase() === selectedDb.toLowerCase();
      const colStr = Array.isArray(ix.columns) ? ix.columns.join(', ') : ix.columns || '';
      const matchSearch =
        !searchQuery.trim() ||
        ix.table.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ix.index.toLowerCase().includes(searchQuery.toLowerCase()) ||
        db.toLowerCase().includes(searchQuery.toLowerCase()) ||
        colStr.toLowerCase().includes(searchQuery.toLowerCase());
      return matchDb && matchSearch;
    });
  }, [indexes, indexFilter, selectedDb, searchQuery]);

  // Filtered Findings
  const filteredFindings = useMemo(() => {
    return (findings?.items || []).filter((f) => {
      const matchDb =
        selectedDb === 'all' ||
        !f.database ||
        f.database.toLowerCase() === selectedDb.toLowerCase();
      const matchSearch =
        !searchQuery.trim() ||
        f.finding.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (f.object && f.object.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (f.database && f.database.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchDb && matchSearch;
    });
  }, [findings, selectedDb, searchQuery]);

  // Filtered Events
  const filteredEvents = useMemo(() => {
    return events.filter((ev) => {
      const matchDb =
        selectedDb === 'all' ||
        !ev.database ||
        ev.database.toLowerCase() === selectedDb.toLowerCase();
      const matchSearch =
        !searchQuery.trim() ||
        ev.event.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ev.details.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ev.database && ev.database.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchDb && matchSearch;
    });
  }, [events, selectedDb, searchQuery]);

  // Status computation
  const overallStatus = useMemo(() => {
    const warnings = kpis?.open_alerts?.warnings ?? (findings?.by_severity?.warning ?? 0);
    const critical = kpis?.open_alerts?.critical ?? (findings?.by_severity?.critical ?? 0);
    if (critical > 0) return { label: `${critical} Critical`, color: 'rose' };
    if (warnings > 0) return { label: `${warnings} Warning${warnings > 1 ? 's' : ''}`, color: 'amber' };
    return { label: 'Healthy', color: 'emerald' };
  }, [kpis, findings]);

  // Copy SQL helper
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCmd(text);
    setTimeout(() => setCopiedCmd(null), 2000);
  };

  /* ─────────────────────────────────────────────────────────────
     CHARTS DATA PREPARATION
     ───────────────────────────────────────────────────────────── */

  // Chart 1: Size by database (Top 6 Doughnut Chart)
  const sizeByDbData = useMemo(() => {
    const list = [...filteredDatabases]
      .filter((d) => (d.size_mb || 0) > 0)
      .sort((a, b) => (b.size_mb || 0) - (a.size_mb || 0))
      .slice(0, 6);
    return list.map((d, idx) => ({
      name: d.database,
      value: Number((d.size_mb || 0).toFixed(3)),
      color: PALETTE[idx % PALETTE.length],
    }));
  }, [filteredDatabases]);

  const totalDbSize = useMemo(() => {
    if (selectedDb !== 'all') {
      const sum = filteredDatabases.reduce((acc, d) => acc + (d.size_mb || 0), 0);
      return sum > 0 ? sum : (kpis?.total_size_mb || 0);
    }
    return kpis?.total_size_mb ?? filteredDatabases.reduce((acc, d) => acc + (d.size_mb || 0), 0);
  }, [selectedDb, kpis, filteredDatabases]);

  // Chart 2: Data, index & free space by database (Stacked Column Bar Chart)
  const dataIndexFreeData = useMemo(() => {
    const list = [...filteredDatabases]
      .filter((d) => (d.size_mb || 0) > 0)
      .sort((a, b) => (b.size_mb || 0) - (a.size_mb || 0))
      .slice(0, 6);
    return list.map((d) => ({
      name: d.database.length > 12 ? `${d.database.slice(0, 10)}..` : d.database,
      fullName: d.database,
      dataMb: Number((d.data_mb || 0).toFixed(3)),
      indexMb: Number((d.index_mb || 0).toFixed(3)),
      freeMb: Number((d.free_mb || 0).toFixed(3)),
    }));
  }, [filteredDatabases]);

  // Chart 4: Largest tables (Top 8 Grouped Horizontal Bar: Data vs Index MB)
  const largestTablesBarData = useMemo(() => {
    const list = [...filteredTables]
      .sort((a, b) => ((b.data_mb || 0) + (b.index_mb || 0)) - ((a.data_mb || 0) + (a.index_mb || 0)))
      .slice(0, 8);
    return list.map((t) => ({
      name: t.table.length > 12 ? `${t.table.slice(0, 10)}..` : t.table,
      displayName: `${t.database}.${t.table}`,
      fullName: `${t.database}.${t.table}`,
      dataMb: Number((t.data_mb || 0).toFixed(3)),
      indexMb: Number((t.index_mb || 0).toFixed(3)),
      totalMb: Number(((t.data_mb || 0) + (t.index_mb || 0)).toFixed(3)),
    }));
  }, [filteredTables]);

  // Chart 5: Average row size (Top 8 Horizontal Bar: bytes per row)
  const avgRowSizeData = useMemo(() => {
    const list = [...filteredTables]
      .filter((t) => (t.avg_row_bytes || 0) > 0)
      .sort((a, b) => (b.avg_row_bytes || 0) - (a.avg_row_bytes || 0))
      .slice(0, 8);
    return list.map((t) => ({
      name: t.table.length > 12 ? `${t.table.slice(0, 10)}..` : t.table,
      displayName: t.table,
      fullName: `${t.database}.${t.table}`,
      avgBytes: t.avg_row_bytes || 0,
      estRows: t.est_rows || 0,
    }));
  }, [filteredTables]);

  // Chart 6: Indexes per database (RADAR CHART as requested by user)
  const radarIndexesData = useMemo(() => {
    const list = [...filteredDatabases]
      .filter((d) => (d.index_count || 0) > 0)
      .sort((a, b) => (b.index_count || 0) - (a.index_count || 0))
      .slice(0, 6);
    return list.map((d) => ({
      database: d.database,
      indexes: d.index_count || 0,
      tables: d.tables || 0,
    }));
  }, [filteredDatabases]);

  // Chart 7: Index reads (Top 8 Horizontal Bar: reads since server start)
  const indexReadsData = useMemo(() => {
    const list = (indexes?.definitions || [])
      .filter((ix) => (ix.reads || 0) > 0)
      .sort((a, b) => (b.reads || 0) - (a.reads || 0))
      .slice(0, 8);
    return list.map((ix) => ({
      name: ix.table.length > 10 ? `${ix.table.slice(0, 8)}..` : ix.table,
      displayName: `${ix.table}.${ix.index}`,
      fullName: `${ix.database ? ix.database + '.' : ''}${ix.table}.${ix.index}`,
      reads: ix.reads || 0,
    }));
  }, [indexes?.definitions]);

  // Chart 8: Tables vs indexes by database (Top 5 Composed Chart: bars = tables, line = indexes)
  const tablesVsIndexesData = useMemo(() => {
    const list = [...filteredDatabases]
      .filter((d) => (d.tables || 0) > 0)
      .sort((a, b) => (b.tables || 0) - (a.tables || 0))
      .slice(0, 5);
    return list.map((d) => ({
      name: d.database.length > 10 ? `${d.database.slice(0, 8)}..` : d.database,
      fullName: d.database,
      tables: d.tables || 0,
      indexes: d.index_count || 0,
    }));
  }, [filteredDatabases]);

  /* ─────────────────────────────────────────────────────────────
     RENDER
     ───────────────────────────────────────────────────────────── */

  if (loading && !data) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 w-64 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-24 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
          <div className="lg:col-span-4 h-64 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
          <div className="lg:col-span-5 h-64 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
          <div className="lg:col-span-3 h-64 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
        </div>
        <div className="h-96 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-8 rounded-xl bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 text-center space-y-4">
        <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto" />
        <div>
          <h3 className="text-base font-semibold text-rose-900 dark:text-rose-200">
            Failed to Load MySQL Storage &amp; Maintenance Overview
          </h3>
          <p className="text-sm text-rose-600 dark:text-rose-400 mt-1 max-w-lg mx-auto">{error}</p>
        </div>
        <button
          onClick={() => fetchData()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold shadow-sm transition-colors cursor-pointer"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-1 sm:p-2">
      {/* ─────────────────────────────────────────────────────────────
          HEADER & TOOLBAR
          ───────────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-orange-500 text-white shadow-sm">
            <HardDrive className="w-4 h-4" />
          </div>
          <div>

            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">
                Storage &amp; Maintenance
              </h2>
              <span className="px-2 py-0.5 text-[11px] font-semibold rounded bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300 uppercase tracking-wide">
                {(dbType || 'MYSQL').toUpperCase()}
              </span>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-200 mt-0.5">
              {coverage?.databases_checked ?? databases.length} databases · {kpis?.tables ?? 0} tables · {kpis?.indexes ?? 0} indexes
              {coverage?.oldest_check ? ` · checked ${formatDateTime(coverage.oldest_check)}` : ''}

            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          {/* Status Badge */}
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border shadow-sm ${overallStatus.color === 'rose'
              ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-300 dark:border-rose-500/30'
              : overallStatus.color === 'amber'
                ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30'
                : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30'
              }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${overallStatus.color === 'rose'
                ? 'bg-rose-500 animate-pulse'
                : overallStatus.color === 'amber'
                  ? 'bg-amber-500'
                  : 'bg-emerald-500'
                }`}
            />
            {overallStatus.label}
          </span>

          {/* Findings Badge Pill */}
          {findings?.count != null && findings.count > 0 && (
            <button
              onClick={() => {
                setActiveTab('findings');
                document.getElementById('table-explorer-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 hover:bg-amber-100 dark:bg-amber-500/10 dark:hover:bg-amber-500/20 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-500/30 transition-colors cursor-pointer"
            >
              <Sparkles className="w-3 h-3 text-amber-500" />
              <span>{findings.count} Finding{findings.count > 1 ? 's' : ''}</span>
            </button>
          )}

          {/* Database Filter CustomDropdown */}
          {dbOptions.length > 0 && (
            <CustomDropdown
              data={dbDropdownOptions}
              selectedValue={selectedDb}
              onSelect={(val) => setSelectedDb(val)}
              width="w-48 sm:w-56"
              placeholder="Select database"
              buttonHeight={8}
            />
          )}

          {/* Reset Filter Button */}
          {selectedDb !== 'all' && (
            <button
              onClick={() => {
                setSelectedDb('all');
                setSearchQuery('');
              }}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-700 transition-colors cursor-pointer"
            >
              Reset
            </button>
          )}


        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 1: 6 TOP KPI CARDS
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        {/* KPI 1: Total size */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Total size</span>
            <Database className="w-3.5 h-3.5 text-blue-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white my-1 font-mono">
            {formatSize(kpis?.total_size_mb)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            data {formatSize(kpis?.data_mb)} · index {formatSize(kpis?.index_mb)}
          </div>
        </div>

        {/* KPI 2: Reclaimable */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Reclaimable</span>
            <HardDrive className="w-3.5 h-3.5 text-amber-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white my-1 font-mono">
            {formatSize(kpis?.reclaimable_mb)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {largestTables.find((t) => (t.free_mb || 0) > 0)
              ? `${largestTables.find((t) => (t.free_mb || 0) > 0)?.database}.${largestTables.find((t) => (t.free_mb || 0) > 0)?.table}`
              : 'InnoDB free blocks'}
          </div>
        </div>

        {/* KPI 3: Tables */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Tables</span>
            <Table className="w-3.5 h-3.5 text-purple-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white my-1 font-mono">
            {formatNumber(kpis?.tables)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {data?.engines?.map((e) => `${e.tables} ${e.engine}`).join(' · ') || 'Active tables'}
          </div>
        </div>

        {/* KPI 4: Indexes */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Indexes</span>
            <TableOfContents className="w-3.5 h-3.5 text-orange-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white my-1 font-mono">
            {formatNumber(kpis?.indexes)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {indexes?.unused ?? 0} unused · {indexes?.redundant ?? 0} redundant
          </div>
        </div>

        {/* KPI 5: Binary Log */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Binary log</span>
            <Layers className="w-3.5 h-3.5 text-purple-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white my-1 font-mono">
            {formatSize(binlog?.size_mb)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {binlog?.enabled ? 'enabled' : 'disabled'} · {binlog?.files ?? 0} files
          </div>
        </div>

        {/* KPI 6: Open Alerts */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Open alerts</span>
            <Zap className="w-3.5 h-3.5 text-amber-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white my-1 font-mono">
            {formatNumber(kpis?.open_alerts?.total ?? findings?.count ?? 0)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {kpis?.open_alerts?.warnings ?? 2} warning · {kpis?.open_alerts?.critical ?? 0} critical
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 2: CHARTS (Size by DB, Data/Index/Free Stacked, Temp tables to disk)
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
        {/* Chart 1: Size by database (Doughnut) */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5 text-orange-500" />
              Size by database
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">doughnut · MB</span>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 my-auto">
            <div className="relative w-36 h-36 flex-shrink-0 flex items-center justify-center">
              <ResponsiveContainer width={144} height={144}>
                <PieChart width={144} height={144}>
                  <Pie
                    data={sizeByDbData}
                    cx="50%"
                    cy="50%"
                    innerRadius={42}
                    outerRadius={65}
                    dataKey="value"
                  >
                    {sizeByDbData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(val: any, name: any) => [`${formatSize(val)}`, name]}
                    contentStyle={chartTheme.tooltip}
                    itemStyle={chartTheme.tooltipItem}
                    labelStyle={chartTheme.tooltipLabel}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xs font-bold text-gray-900 dark:text-white font-mono leading-tight text-center max-w-[76px] truncate" title={formatSize(totalDbSize)}>
                  {formatSize(totalDbSize)}
                </span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">total</span>
              </div>
            </div>
            <div className="flex-1 w-full space-y-1.5 max-h-[150px] overflow-y-auto pr-1">
              {sizeByDbData.map((d) => (
                <div key={d.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5 truncate max-w-[110px]">
                    <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: d.color }} />
                    <span className="text-gray-800 dark:text-gray-200 truncate" title={d.name}>
                      {d.name}
                    </span>
                  </div>
                  <span className="font-mono text-gray-500 dark:text-gray-200 text-[11px]">
                    {formatSize(d.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Chart 2: Data, index & free space by database (Stacked Column) */}
        <div className="lg:col-span-5 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <HardDrive className="w-3.5 h-3.5 text-blue-500" />
              Data, index &amp; free space by database
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">stacked column · MB</span>
          </div>

          <div className="h-60 w-full flex-1" style={{ minHeight: '210px' }}>
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={dataIndexFreeData} margin={{ top: 10, right: 15, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} opacity={chartTheme.gridOpacity} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: chartTheme.axisText }} stroke={chartTheme.axisStroke} />
                <YAxis unit=" MB" tick={{ fontSize: 10, fill: chartTheme.axisText }} stroke={chartTheme.axisStroke} />
                <RechartsTooltip
                  formatter={(val: any, name: any) => [`${formatSize(val)}`, name]}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                  cursor={{ fill: isDark ? '#334155' : '#e2e8f0', opacity: 0.3 }}
                />
                <Bar dataKey="dataMb" name="Data" stackId="a" fill="#0b6fa4" radius={[0, 0, 0, 0]} maxBarSize={32} />
                <Bar dataKey="indexMb" name="Index" stackId="a" fill="#7a4fc4" radius={[0, 0, 0, 0]} maxBarSize={32} />
                <Bar dataKey="freeMb" name="Free (reclaimable)" stackId="a" fill="#e08a1e" radius={[3, 3, 0, 0]} maxBarSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="flex items-center flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-200 pt-2 border-t border-gray-100 dark:border-gray-700/60 mt-1">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#0b6fa4]" />
              <span>Data</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#7a4fc4]" />
              <span>Index</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#e08a1e]" />
              <span>Free (reclaimable)</span>
            </span>
          </div>
        </div>

        {/* Widget 3: Temp tables to disk (Bullet bar & Mini Specs) */}
        <div className="lg:col-span-3 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-amber-500" />
              Temp tables to disk
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">scale 0–50%</span>
          </div>

          <div className="py-2">
            <div className="flex justify-between items-baseline mb-1.5">
              <span className="text-2xl font-bold font-mono text-gray-900 dark:text-white">
                {data?.temp_tables_to_disk_pct != null ? `${data.temp_tables_to_disk_pct}%` : '21.89%'}
              </span>
              <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">warn at 25%</span>
            </div>

            {/* Bullet bar container */}
            <div className="relative h-5 rounded-md overflow-hidden bg-gray-200 dark:bg-gray-700 flex">
              <div className="w-1/2 h-full bg-emerald-500/20" title="Healthy region (0-25%)" />
              <div className="w-1/2 h-full bg-amber-500/20" title="Warning region (25-50%)" />
              {/* Fill bar */}
              <div
                className="absolute top-1 bottom-1 left-0 bg-blue-600 dark:bg-blue-500 rounded-r transition-all duration-500"
                style={{
                  width: `${Math.min(100, ((data?.temp_tables_to_disk_pct ?? 21.89) / 50) * 100)}%`,
                }}
              />
              {/* Target / warning marker at 50% (25%) */}
              <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-gray-800 dark:bg-gray-200" title="Warn marker (25%)" />
            </div>

            <div className="flex justify-between text-[10px] text-gray-400 mt-1 font-mono">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
            </div>
          </div>

          <div className="border-t border-gray-100 dark:border-gray-700/60 pt-2 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">History list length</span>
              <span className="font-mono font-medium text-gray-800 dark:text-gray-200">
                {innodb?.history_list_length ?? 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">System tablespace</span>
              <span className="font-mono font-medium text-gray-800 dark:text-gray-200">
                {innodb?.system_tablespace_mb ? `${innodb.system_tablespace_mb} MB` : '12 MB'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">File per table</span>
              <span className="font-mono font-medium text-gray-800 dark:text-gray-200">
                {innodb?.file_per_table ? 'on' : 'off'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Binary log expiry</span>
              <span className="font-mono font-medium text-gray-800 dark:text-gray-200">
                {binlog?.expire_sec ? `${Math.round(binlog.expire_sec / 86400)} days` : '30 days'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Persistent stats missing</span>
              <span className="font-mono font-medium text-gray-800 dark:text-gray-200">
                {data?.missing_persistent_stats ?? 0} tables
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 3: CHARTS (Largest tables, Average row size, Indexes per DB Radar)
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
        {/* Chart 4: Largest tables (Grouped Horizontal Bar) */}
        <div className="lg:col-span-5 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Table className="w-3.5 h-3.5 text-orange-500" />
              Largest tables
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">top 8 · grouped bar · MB</span>
          </div>

          <div className="h-64 w-full flex-1" style={{ minHeight: '230px' }}>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart layout="vertical" data={largestTablesBarData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} opacity={chartTheme.gridOpacity} horizontal={false} />
                <XAxis type="number" unit=" MB" tick={{ fontSize: 10, fill: chartTheme.axisText }} stroke={chartTheme.axisStroke} />
                <YAxis
                  type="category"
                  dataKey="displayName"
                  interval={0}
                  width={110}
                  tick={{ fontSize: 9.5, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                />
                <RechartsTooltip
                  formatter={(val: any, name: any) => [`${formatSize(val)}`, name]}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                  cursor={{ fill: isDark ? '#334155' : '#e2e8f0', opacity: 0.3 }}
                />
                <Bar dataKey="dataMb" name="Data" fill="#0b6fa4" radius={[0, 2, 2, 0]} maxBarSize={11} />
                <Bar dataKey="indexMb" name="Index" fill="#7a4fc4" radius={[0, 2, 2, 0]} maxBarSize={11} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="flex items-center flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-200 pt-2 border-t border-gray-100 dark:border-gray-700/60 mt-1">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#0b6fa4]" />
              <span>Data MB</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#7a4fc4]" />
              <span>Index MB</span>
            </span>
          </div>
        </div>

        {/* Chart 5: Average row size (Horizontal Bar) */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-blue-500" />
              Average row size
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">top 8 · bytes</span>
          </div>

          <div className="h-64 w-full flex-1" style={{ minHeight: '230px' }}>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart layout="vertical" data={avgRowSizeData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} opacity={chartTheme.gridOpacity} horizontal={false} />
                <XAxis type="number" unit=" B" tick={{ fontSize: 10, fill: chartTheme.axisText }} stroke={chartTheme.axisStroke} />
                <YAxis
                  type="category"
                  dataKey="displayName"
                  interval={0}
                  width={90}
                  tick={{ fontSize: 9.5, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                />
                <RechartsTooltip
                  formatter={(val: any) => [`${formatNumber(val)} Bytes`, 'Avg Row Size']}
                  labelFormatter={(_label: any, payload: any) => {
                    const item = payload?.[0]?.payload;
                    return item ? `${item.fullName} (${formatNumber(item.estRows)} rows)` : _label;
                  }}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                  cursor={{ fill: isDark ? '#334155' : '#e2e8f0', opacity: 0.3 }}
                />
                <Bar dataKey="avgBytes" name="Avg row bytes" fill="#e08a1e" radius={[0, 4, 4, 0]} maxBarSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-200 pt-2 border-t border-gray-100 dark:border-gray-700/60 mt-1">
            Calculated from InnoDB data length ÷ estimated table rows
          </div>
        </div>

        {/* Chart 6: Indexes per database (RADAR CHART - user requested Simple Radar Chart) */}
        <div className="lg:col-span-3 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <TableOfContents className="w-3.5 h-3.5 text-orange-500" />
              Indexes per database
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">radar</span>
          </div>

          <div className="h-64 w-full flex-1 flex items-center justify-center" style={{ minHeight: '230px' }}>
            <ResponsiveContainer width="100%" height={230}>
              <RadarChart outerRadius={72} data={radarIndexesData}>
                <PolarGrid stroke={chartTheme.grid} />
                <PolarAngleAxis dataKey="database" tick={{ fontSize: 9.5, fill: chartTheme.axisText }} />
                <PolarRadiusAxis tick={{ fontSize: 8.5, fill: chartTheme.axisText }} stroke={chartTheme.axisStroke} />
                <Radar
                  name="Indexes"
                  dataKey="indexes"
                  stroke="#f97316"
                  fill="#f97316"
                  fillOpacity={0.4}
                />
                <RechartsTooltip
                  formatter={(val: any) => [`${val} indexes`, 'Index Count']}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60 text-[11px] text-gray-500 dark:text-gray-400 flex justify-between">
            <span>Total index count:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
              {kpis?.indexes || 0}
            </span>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 4: CHARTS (Index reads, Tables vs Indexes)
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
        {/* Chart 7: Index reads (Top 8 Horizontal Bar) */}
        <div className="lg:col-span-6 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <TableOfContents className="w-3.5 h-3.5 text-purple-500" />
              Index reads
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">top 8 · since server start</span>
          </div>

          <div className="h-56 w-full flex-1" style={{ minHeight: '190px' }}>
            <ResponsiveContainer width="100%" height={190}>
              <BarChart layout="vertical" data={indexReadsData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} opacity={chartTheme.gridOpacity} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: chartTheme.axisText }} stroke={chartTheme.axisStroke} />
                <YAxis
                  type="category"
                  dataKey="displayName"
                  interval={0}
                  width={130}
                  tick={{ fontSize: 9.5, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                />
                <RechartsTooltip
                  formatter={(val: any) => [`${formatNumber(val)} reads`, 'Reads']}
                  labelFormatter={(_label: any, payload: any) => {
                    const item = payload?.[0]?.payload;
                    return item?.fullName || _label;
                  }}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                  cursor={{ fill: isDark ? '#334155' : '#e2e8f0', opacity: 0.3 }}
                />
                <Bar dataKey="reads" name="Reads" fill="#7a4fc4" radius={[0, 4, 4, 0]} maxBarSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 8: Tables vs indexes by database (Dual Axis Composed Chart) */}
        <div className="lg:col-span-6 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5 text-blue-500" />
              Tables vs indexes by database
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">bars = tables · line = indexes</span>
          </div>

          <div className="h-56 w-full flex-1" style={{ minHeight: '190px' }}>
            <ResponsiveContainer width="100%" height={190}>
              <ComposedChart data={tablesVsIndexesData} margin={{ top: 10, right: 5, left: -10, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} opacity={chartTheme.gridOpacity} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 9.5, fill: chartTheme.axisText }} stroke={chartTheme.axisStroke} />
                <YAxis yAxisId="left" tick={{ fontSize: 9.5, fill: chartTheme.axisText }} stroke={chartTheme.axisStroke} />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  width={24}
                  tickMargin={2}
                  tick={{ fontSize: 9.5, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                  allowDecimals={false}
                />
                <RechartsTooltip
                  formatter={(val: any, name: any) => [`${val}`, name]}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                />
                <Bar yAxisId="left" dataKey="tables" name="Tables" fill="#0b6fa4" radius={[3, 3, 0, 0]} maxBarSize={20} />
                <Line yAxisId="right" type="monotone" dataKey="indexes" name="Indexes" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 5: INTERACTIVE DATA TABLES / TABS EXPLORER
          ───────────────────────────────────────────────────────────── */}
      <div
        id="table-explorer-section"
        className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden"
      >
        {/* Navigation Tabs */}
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 pt-3 pb-0 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
            <button
              onClick={() => setActiveTab('databases')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'databases'
                ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
            >
              <Database className="w-3.5 h-3.5" />
              Databases ({filteredDatabases.length})
            </button>

            <button
              onClick={() => setActiveTab('tables')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'tables'
                ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
            >
              <Table className="w-3.5 h-3.5" />
              Largest Tables ({filteredTables.length})
            </button>

            <button
              onClick={() => setActiveTab('indexes')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'indexes'
                ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
            >
              <TableOfContents className="w-3.5 h-3.5" />
              Index Inventory ({filteredIndexes.length})
            </button>

            <button
              onClick={() => setActiveTab('findings')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'findings'
                ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              Findings ({filteredFindings.length})
            </button>

            <button
              onClick={() => setActiveTab('coverage')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'coverage'
                ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
            >
              <Server className="w-3.5 h-3.5" />
              Coverage &amp; Thresholds
            </button>

            {events.length > 0 && (
              <button
                onClick={() => setActiveTab('events')}
                className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'events'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
              >
                <Clock className="w-3.5 h-3.5" />
                Events ({filteredEvents.length})
              </button>
            )}
          </div>
        </div>

        {/* ── TAB 1: DATABASES ── */}
        {activeTab === 'databases' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter databases..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500"
                />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-200 font-medium">
                Showing {filteredDatabases.length} database{filteredDatabases.length === 1 ? '' : 's'}
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-500 dark:text-gray-200 font-medium border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="py-2.5 px-3">Database</th>
                    <th className="py-2.5 px-3">Status</th>
                    <th className="py-2.5 px-3 text-right">Total MB</th>
                    <th className="py-2.5 px-3 text-right">Data MB</th>
                    <th className="py-2.5 px-3 text-right">Index MB</th>
                    <th className="py-2.5 px-3 text-right">Free MB</th>
                    <th className="py-2.5 px-3 text-right">Tables</th>
                    <th className="py-2.5 px-3 text-right">Indexes</th>
                    <th className="py-2.5 px-3 text-right">Issues</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredDatabases.map((d) => {
                    const issuesCount = (d.tables_without_pk || 0) + (d.redundant_indexes || 0) + (d.unused_indexes || 0);
                    return (
                      <tr key={d.database} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="py-2.5 px-3 font-semibold text-gray-900 dark:text-white">
                          <span className="flex items-center gap-1.5">
                            {d.database}
                            {d.is_system && (
                              <span className="text-[10px] text-gray-400 font-normal font-sans">(sys)</span>
                            )}
                          </span>
                        </td>
                        <td className="py-2.5 px-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${d.status === 'warning'
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                              : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                              }`}
                          >
                            {d.status || 'healthy'}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono font-semibold text-gray-900 dark:text-white">
                          {(d.size_mb || 0).toFixed(2)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-gray-300">
                          {(d.data_mb || 0).toFixed(2)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-gray-300">
                          {(d.index_mb || 0).toFixed(2)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-gray-300">
                          {(d.free_mb || 0) > 0 ? (
                            <span className="text-amber-600 dark:text-amber-400 font-semibold">
                              {(d.free_mb || 0).toFixed(2)}
                            </span>
                          ) : (
                            '0'
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-gray-300">
                          {formatNumber(d.tables)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-gray-300">
                          {formatNumber(d.index_count)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono">
                          {issuesCount > 0 ? (
                            <span className="px-1.5 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                              {issuesCount}
                            </span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── TAB 2: LARGEST TABLES ── */}
        {activeTab === 'tables' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter tables by name or engine..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500"
                />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-200 font-medium">
                Showing {filteredTables.length} table{filteredTables.length === 1 ? '' : 's'}
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-500 dark:text-gray-200 font-medium border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="py-2.5 px-3">Table</th>
                    <th className="py-2.5 px-3">Database</th>
                    <th className="py-2.5 px-3">Engine</th>
                    <th className="py-2.5 px-3 text-right">Data MB</th>
                    <th className="py-2.5 px-3 text-right">Index MB</th>
                    <th className="py-2.5 px-3 text-right">Free MB</th>
                    <th className="py-2.5 px-3 text-right">Est. Rows</th>
                    <th className="py-2.5 px-3 text-right">Avg Row (B)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredTables.map((t, idx) => (
                    <tr key={`${t.database}.${t.table}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td className="py-2.5 px-3 font-semibold text-gray-900 dark:text-white">
                        {t.table}
                      </td>
                      <td className="py-2.5 px-3 text-gray-500 dark:text-gray-200 font-mono text-[11px]">
                        {t.database}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-gray-700 dark:text-gray-300">
                        {t.engine || 'InnoDB'}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-800 dark:text-gray-200">
                        {(t.data_mb || 0).toFixed(3)}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-purple-600 dark:text-purple-400">
                        {(t.index_mb || 0).toFixed(3)}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono">
                        {(t.free_mb || 0) > 0 ? (
                          <span className="px-1.5 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                            {(t.free_mb || 0).toFixed(1)} MB
                          </span>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-gray-300">
                        {formatNumber(t.est_rows)}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-gray-300">
                        {formatNumber(t.avg_row_bytes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── TAB 3: INDEX INVENTORY ── */}
        {activeTab === 'indexes' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative max-w-sm">
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search index, table, columns..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-4 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500"
                  />
                </div>

                <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 border border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setIndexFilter('all')}
                    className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${indexFilter === 'all'
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white'
                      }`}
                  >
                    All ({indexes?.total ?? filteredIndexes.length})
                  </button>
                  <button
                    onClick={() => setIndexFilter('unused')}
                    className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${indexFilter === 'unused'
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white'
                      }`}
                  >
                    Unused ({indexes?.unused ?? 0})
                  </button>
                  <button
                    onClick={() => setIndexFilter('redundant')}
                    className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${indexFilter === 'redundant'
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white'
                      }`}
                  >
                    Redundant ({indexes?.redundant ?? 0})
                  </button>
                </div>
              </div>

              <div className="text-xs text-gray-500 dark:text-gray-200 font-medium">
                Showing {filteredIndexes.length} index{filteredIndexes.length === 1 ? '' : 'es'}
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-500 dark:text-gray-200 font-medium border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="py-2.5 px-3">Table</th>
                    <th className="py-2.5 px-3">Index</th>
                    <th className="py-2.5 px-3">Database</th>
                    <th className="py-2.5 px-3">Columns</th>
                    <th className="py-2.5 px-3 text-right">Cardinality</th>
                    <th className="py-2.5 px-3 text-right">Reads</th>
                    <th className="py-2.5 px-3 text-right">Writes</th>
                    <th className="py-2.5 px-3 text-right">MB</th>
                    <th className="py-2.5 px-3">Flag</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredIndexes.map((ix, idx) => {
                    const colStr = Array.isArray(ix.columns) ? ix.columns.join(', ') : ix.columns || '–';
                    return (
                      <tr key={`${ix.database}.${ix.table}.${ix.index}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="py-2.5 px-3 font-semibold text-gray-900 dark:text-white">
                          {ix.table}
                        </td>
                        <td className="py-2.5 px-3 font-mono font-medium text-blue-600 dark:text-blue-400">
                          {ix.index}
                        </td>
                        <td className="py-2.5 px-3 text-gray-500 dark:text-gray-200 font-mono text-[11px]">
                          {ix.database || '–'}
                        </td>
                        <td className="py-2.5 px-3 font-mono text-[11px] text-gray-600 dark:text-gray-200 max-w-xs truncate" title={colStr}>
                          <code>{colStr}</code>
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-gray-300">
                          {formatNumber(ix.cardinality)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-gray-300">
                          {formatNumber(ix.reads)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-gray-300">
                          {formatNumber(ix.writes)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-purple-600 dark:text-purple-400">
                          {ix.size_mb != null ? ix.size_mb.toFixed(3) : '–'}
                        </td>
                        <td className="py-2.5 px-3">
                          {ix.flag === 'unused' ? (
                            <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400">
                              Unused
                            </span>
                          ) : ix.flag === 'redundant' ? (
                            <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                              Redundant
                            </span>
                          ) : ix.flag === 'unique' ? (
                            <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                              Unique
                            </span>
                          ) : (
                            <span className="text-gray-400 text-[11px]">Standard</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── TAB 4: FINDINGS & RECOMMENDATIONS ── */}
        {activeTab === 'findings' && (
          <div className="p-4 space-y-4">
            <div className="p-3.5 rounded-lg bg-amber-50 dark:bg-amber-950/20 text-xs text-amber-800 dark:text-amber-300">
              <p className="font-semibold mb-0.5">Automated Read-Only Agent Findings</p>
              <p className="text-amber-700 dark:text-amber-400">
                {findings?.note ||
                  'These are automated findings from the monitoring agent. Check each one and decide before taking action; rebuilds, VACUUM, and OPTIMIZE lock or load the server.'}
              </p>
            </div>

            <div className="space-y-3">
              {filteredFindings.length === 0 ? (
                <div className="p-8 text-center text-gray-200 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No active findings or alerts</p>
                  <p className="text-xs text-gray-500 dark:text-gray-200 mt-1">All storage and maintenance checks passed successfully.</p>
                </div>
              ) : (
                filteredFindings.map((f, idx) => (
                  <div
                    key={`finding-${idx}`}
                    className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm space-y-2.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider ${f.severity === 'critical'
                            ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
                            : f.severity === 'warning'
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            }`}
                        >
                          {f.severity}
                        </span>
                        {f.database && (
                          <span className="px-2 py-0.5 rounded text-[11px] font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                            {f.database}
                          </span>
                        )}
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          {f.finding}
                        </span>
                      </div>
                    </div>

                    {f.impact && (
                      <p className="text-xs text-gray-600 dark:text-gray-300">
                        <strong className="text-gray-700 dark:text-gray-200">Impact: </strong>
                        {f.impact}
                      </p>
                    )}

                    {f.remediation && (
                      <p className="text-xs text-gray-600 dark:text-gray-300">
                        <strong className="text-gray-700 dark:text-gray-200">Recommendation: </strong>
                        {f.remediation}
                      </p>
                    )}

                    {f.command && (
                      <div className="relative group mt-2">
                        <pre className="p-2.5 rounded-lg bg-gray-900 text-gray-100 dark:bg-gray-950 font-mono text-xs overflow-x-auto border border-gray-800">
                          <code>{f.command}</code>
                        </pre>
                        <button
                          onClick={() => handleCopy(f.command!)}
                          className="absolute right-2 top-2 p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors cursor-pointer"
                          title="Copy command"
                        >
                          {copiedCmd === f.command ? (
                            <Check className="w-3.5 h-3.5 text-emerald-400" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── TAB 5: COVERAGE & THRESHOLDS ── */}
        {activeTab === 'coverage' && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">Databases Checked</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                  {coverage?.databases_checked ?? databases.length} / {coverage?.databases ?? databases.length}
                </span>
              </div>
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">Check Interval</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                  {coverage?.interval_hours ? `Every ${coverage.interval_hours} hour(s)` : 'Hourly'}
                </span>
              </div>
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">Tables Without Primary Key</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                  {databases.reduce((acc, d) => acc + (d.tables_without_pk || 0), 0)} (heartbeat)
                </span>
              </div>
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">Fragmented Tables</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                  {data?.fragmentation?.tables ?? 0}
                </span>
              </div>
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">Free Space in InnoDB Files</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                  {formatSize(data?.fragmentation?.reclaimable_mb ?? kpis?.reclaimable_mb)}
                </span>
              </div>
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">Fragmentation Alert</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                  ≥{data?.thresholds?.fragmented_min_free_mb ?? 10} MB &amp; ≥{data?.thresholds?.fragmented_min_pct ?? 25}%
                </span>
              </div>
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">Auto-increment Warn / Critical</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                  {data?.thresholds?.auto_increment_warn_pct ?? 75}% / {data?.thresholds?.auto_increment_critical_pct ?? 90}%
                </span>
              </div>
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">History List Warn</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                  {formatNumber(data?.thresholds?.history_list_warn ?? 1000000)}
                </span>
              </div>
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">Temp Tables to Disk Warn</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                  {data?.thresholds?.temp_tables_to_disk_warn_pct ?? 25}%
                </span>
              </div>
            </div>

            {data?.auto_increment?.reason && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-800 dark:text-amber-200">
                <span className="font-semibold text-amber-900 dark:text-amber-100">Visibility note: </span>
                <span className="text-[11px] opacity-90">{data.auto_increment.reason}</span>
              </div>
            )}
          </div>
        )}

        {/* ── TAB 6: EVENTS ── */}
        {activeTab === 'events' && (
          <div className="p-4 space-y-3">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-500 dark:text-gray-200 font-medium border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="py-2.5 px-3">Time</th>
                    <th className="py-2.5 px-3">Database</th>
                    <th className="py-2.5 px-3">Event</th>
                    <th className="py-2.5 px-3">Severity</th>
                    <th className="py-2.5 px-3">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredEvents.map((ev, idx) => (
                    <tr key={`ev-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td className="py-2.5 px-3 text-gray-500 dark:text-gray-200 font-mono text-[11px] whitespace-nowrap">
                        {formatDateTime(ev.time)}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-[11px] text-gray-700 dark:text-gray-300">
                        {ev.database || '–'}
                      </td>
                      <td className="py-2.5 px-3 font-semibold text-gray-900 dark:text-white">
                        {ev.event}
                      </td>
                      <td className="py-2.5 px-3">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${ev.severity === 'warning'
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                            }`}
                        >
                          {ev.severity}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300 font-mono text-[11px]">
                        {ev.details}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MySqlStorage;
